import React from "react";
import SortableHeader from "../../common-components/SortableHeader";
import {
  InfoCircleIcon,
  CalendarIcon,
  ClockIcon,
  PersonIcon,
  DatePickerIcon,
} from "../../../images/icons/icons";
import InfoPopover from "./re-usablee/InfoPopover";
import {
  formatDateTimezon,
  convertTo12HourFormat,
  convertTime,
} from "../../common-data/Converts";
import {
  CheckCircleIcon,
  XCircleIcon,
  CloudArrowIcon,
} from "../../../images/icons/icons";

/**
 * Creates sortable table columns for PT Reports
 * @param {Object} params - Parameters for column creation
 * @param {Function} params.handleSort - Sort handler function
 * @param {Function} params.getSortDirection - Get sort direction function
 * @param {Function} params.isFieldSorted - Check if field is sorted function
 * @param {Function} params.handleInfoClick - Info click handler
 * @param {Function} params.setClickValues - Set click values function
 * @param {Function} params.onClickBtns - Button click handler
 * @param {Function} params.handleDelete - Delete handler
 * @param {Object} params.activePopover - Active popover state
 * @param {Function} params.setActivePopover - Set active popover function
 * @param {boolean} params.isTrainer - Is trainer flag
 * @param {boolean} params.isAdmin - Is admin flag
 * @param {boolean} params.isFitnessManager - Is fitness manager flag
 * @param {boolean} params.isReceptionist - Is receptionist flag
 * @returns {Array} - Array of column definitions
 */

export const createSortableColumns = ({
  handleSort,
  getSortDirection,
  isFieldSorted,
  handleInfoClick,
  setClickValues,
  activePopover,
  setActivePopover,
}) => {
  return [
    {
      dataField: "last_checkin",
      text: (
        <SortableHeader
          text="Member Last Checkin"
          field="last_checkin"
          onSort={handleSort}
          sortDirection={getSortDirection("last_checkin")}
          isSorted={isFieldSorted("last_checkin")}
        />
      ),
      formatter: (_, row) => {
        return (
          <div className="tb-flex">
            <span
              className="tb-data tb-link tb-info-icon"
              onClick={(e) => handleInfoClick(row, "checkin", e)}
            >
              <InfoCircleIcon />
              <InfoPopover
                isVisible={activePopover === `${row.uid}-checkin`}
                onClose={() => setActivePopover(null)}
                title="Member Last Check-in"
                data={[
                  {
                    label: "Date",
                    value: formatDateTimezon(row?.last_checkin),
                    fallback: "Never",
                    icon: <CalendarIcon color="#2c3e50" />,
                  },
                  {
                    label: "Time",
                    value: convertTo12HourFormat(row?.checkin_time),
                    fallback: "N/A",
                    icon: <ClockIcon color="#2c3e50" />,
                  },
                  {
                    label: "Days Ago",
                    value: row?.last_checkin_days_ago?.replace(/_/g, " "),
                    fallback: "N/A",
                    icon: <PersonIcon color="#2c3e50" />,
                  },
                ]}
                position="bottom"
              />
            </span>

            <span
              className="tb-data tb-link"
              onClick={() =>
                setClickValues((prevs) => ({
                  ...prevs,
                  userId: row?.user_id,
                  type: "member",
                  isModOpen: true,
                  name: "checkinLogsModal",
                }))
              }
            >
              {row?.last_checkin ? formatDateTimezon(row.last_checkin) : "-"}{" "}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "last_workout_date",
      text: (
        <SortableHeader
          text="Last Workout"
          field="last_workout_date"
          onSort={handleSort}
          sortDirection={getSortDirection("last_workout_date")}
          isSorted={isFieldSorted("last_workout_date")}
        />
      ),
      formatter: (_, row) => {
        return (
          <span
            className="tb-data tb-link"
            onClick={() =>
              setClickValues((prevs) => ({
                ...prevs,
                userId: row?.user_id,
                isModOpen: true,
                name: "workoutLogModal",
                workoutDate: row?.last_workout_date
                  ? formatDateTimezon(row.last_workout_date)
                  : "",
              }))
            }
          >
            {row?.last_workout_date
              ? formatDateTimezon(row.last_workout_date)
              : "-"}
          </span>
        );
      },
    },
    {
      dataField: "assigned_to_last_checkin",
      text: (
        <SortableHeader
          text="Assign To Last Checkin"
          field="assigned_to_last_checkin"
          onSort={handleSort}
          sortDirection={getSortDirection("assigned_to_last_checkin")}
          isSorted={isFieldSorted("assigned_to_last_checkin")}
        />
      ),
      formatter: (_, row) => {
        return (
          <div className="tb-flex">
            <span
              className="tb-data tb-link tb-info-icon"
              onClick={(e) => handleInfoClick(row, "trainer-checkin", e)}
            >
              <InfoCircleIcon />
              <InfoPopover
                isVisible={activePopover === `${row.uid}-trainer-checkin`}
                onClose={() => setActivePopover(null)}
                title="Trainer Last Checkin"
                data={[
                  {
                    label: "Date",
                    value: formatDateTimezon(row.assigned_to_last_checkin),
                    fallback: "-",
                    icon: <DatePickerIcon color="#2c3e50" />,
                  },
                  {
                    label: "Time",
                    value: convertTime(row.assigned_to_checkin_time),
                    fallback: "-",
                    icon: <ClockIcon color="#2c3e50" />,
                  },
                  {
                    label: "Days Ago",
                    value: row?.assigned_to_last_checkin_days_ago?.replace(
                      /_/g,
                      " "
                    ),
                    fallback: "-",
                    icon: <PersonIcon color="#2c3e50" />,
                  },
                ]}
                position="bottom"
              />
            </span>

            <span
              className="tb-data tb-link"
              onClick={() =>
                setClickValues((prevs) => ({
                  ...prevs,
                  isModOpen: true,
                  name: "checkinLogsModal",
                  userId: row?.assigned_to,
                  type: "trainer",
                }))
              }
            >
              {row?.assigned_to_last_checkin
                ? formatDateTimezon(row.assigned_to_last_checkin)
                : "-"}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "assigned_date",
      text: (
        <SortableHeader
          text="Handover Date"
          field="assigned_date"
          onSort={handleSort}
          sortDirection={getSortDirection("assigned_date")}
          isSorted={isFieldSorted("assigned_date")}
        />
      ),
      formatter: (_, row) => {
        return (
          <div className="tb-flex">
            <span className="tb-data tb-icon">
              {row?.handover_status === "completed" ? (
                <CheckCircleIcon />
              ) : (
                <XCircleIcon />
              )}
            </span>
            <span className="tb-data">
              {row?.assigned_date ? formatDateTimezon(row.assigned_date) : "-"}
            </span>
          </div>
        );
      },
    },
    {
      dataField: "pt_initiate_date",
      text: (
        <SortableHeader
          text="First BCA"
          field="pt_initiate_date"
          onSort={handleSort}
          sortDirection={getSortDirection("pt_initiate_date")}
          isSorted={isFieldSorted("pt_initiate_date")}
        />
      ),
      formatter: (_, row) => {
        return (
          <div className="tb-flex">
            {row?.bca_report ? (
              <a
                href={row.bca_report}
                target="_blank"
                rel="noopener noreferrer"
                className="tb-data tb-icon"
              >
                <CloudArrowIcon color="#3396D3" />
              </a>
            ) : (
              <span className="tb-data tb-icon">
                <XCircleIcon />
              </span>
            )}

            <span className="tb-data">
              {row?.pt_initiate_date
                ? formatDateTimezon(row.pt_initiate_date)
                : "-"}
            </span>
          </div>
        );
      },
    },
  ];
};
